import type { AppLanguage } from "../../lib/preferences";

const englishCopy = {
  readViews: "Notification status",
  unreadView: "Unread",
  allUpdates: "All",
  allVenues: "Across all venues",
  clearing: "Marking as read…",
  caughtUp: "You’re all caught up",
  noUnreadInView: "No unread updates in this view",
  checkEarlier: "Load earlier notifications to check older updates.",
  readHistory: "Read notifications stay available under All.",
  readError: "Could not save the read status. Your unread updates are still here. Try again.",
  account: "Account",
  accountDescription: "Security and account access changes.",
  accountUpdate: "Account update",
  all: "All",
  allTypes: "All types",
  approval: "Approvals",
  approvalDescription: "Provider and venue review decisions.",
  approvalUpdate: "Approval update",
  booking: "Bookings",
  bookingDescription: "New requests and booking changes.",
  bookingUpdate: "Booking update",
  categories: "Notification categories",
  clearFilter: "Show all",
  dismissPending: "Dismiss pending booking alert",
  emptyBody: "Booking, session and order updates will appear here.",
  emptyFiltered: "No {category} notifications in this view.",
  emptyTitle: "No notifications yet.",
  errorBody: "Check your connection and try again.",
  errorTitle: "Notifications could not be refreshed",
  food_order: "Orders",
  food_orderDescription: "Food order progress from the floor.",
  food_orderUpdate: "Order update",
  inbox: "Inbox",
  inboxAlwaysOn: "Inbox records stay available even when push is off.",
  invalidTarget: "This update does not have a safe destination. It stays here for reference.",
  justNow: "Just now",
  loadEarlier: "Load earlier notifications",
  loadMoreFailed: "Earlier notifications could not be loaded.",
  loading: "Loading notifications…",
  loadingEarlier: "Loading earlier notifications…",
  markAllRead: "Mark all read",
  markRead: "Mark as read",
  notificationViews: "Notification views",
  notifications: "Notifications",
  openBooking: "Open booking",
  openDetails: "Review details",
  openOrder: "Open order",
  openSession: "Open session",
  openVenue: "Open venue",
  pendingBody: "Customers cannot check in until the remaining bookings are confirmed.",
  pendingEyebrow: "Action required",
  pendingMany: "{count} bookings awaiting confirmation",
  pendingOne: "{count} booking awaiting confirmation",
  preferenceError: "Push settings could not be saved. Your previous choices are still in effect.",
  preferenceLoadError: "Push settings could not be loaded.",
  pushAlerts: "Push alerts",
  pushBody: "Choose which updates should reach this account away from the workspace.",
  pushLoading: "Loading push settings…",
  pushTitle: "Push settings",
  refresh: "Refresh notifications",
  refreshing: "Refreshing notifications…",
  retry: "Try again",
  review: "Review",
  reviewPending: "Review bookings",
  saved: "Push settings are up to date.",
  saving: "Saving push setting…",
  session: "Sessions",
  sessionDescription: "Session starts, extensions and endings.",
  sessionUpdate: "Session update",
  staleTarget: "This update is no longer actionable. It stays here for reference.",
  turnOff: "Turn off {category} push alerts",
  turnOn: "Turn on {category} push alerts",
  unauthorizedTarget: "Access changed, so this destination is no longer available.",
  unavailable: "Unavailable",
  unread: "{count} unread",
} as const;

type ProviderNotificationCopyKey = keyof typeof englishCopy;

const arabicCopy: Record<ProviderNotificationCopyKey, string> = {
  readViews: "حالة الإشعارات",
  unreadView: "غير مقروء",
  allUpdates: "الكل",
  allVenues: "من جميع الأماكن",
  clearing: "جار وضع علامة مقروء…",
  caughtUp: "أنت متابع لكل التحديثات",
  noUnreadInView: "لا توجد تحديثات غير مقروءة في هذا العرض",
  checkEarlier: "حمّل الإشعارات الأقدم للاطلاع على باقي التحديثات.",
  readHistory: "تظل الإشعارات المقروءة متاحة في «الكل».",
  readError: "تعذر حفظ حالة القراءة. إشعاراتك غير المقروءة ما زالت هنا. حاول مرة أخرى.",
  account: "الحساب",
  accountDescription: "تغييرات الأمان والوصول إلى الحساب.",
  accountUpdate: "تحديث الحساب",
  all: "الكل",
  allTypes: "كل الأنواع",
  approval: "الاعتمادات",
  approvalDescription: "قرارات مراجعة المزود والمكان.",
  approvalUpdate: "تحديث اعتماد",
  booking: "الحجوزات",
  bookingDescription: "الطلبات الجديدة وتغييرات الحجز.",
  bookingUpdate: "تحديث حجز",
  categories: "فئات الإشعارات",
  clearFilter: "عرض الكل",
  dismissPending: "تجاهل تنبيه الحجوزات المعلقة",
  emptyBody: "ستظهر هنا تحديثات الحجوزات والجلسات والطلبات.",
  emptyFiltered: "لا توجد إشعارات ضمن فئة {category} في هذا العرض.",
  emptyTitle: "لا توجد إشعارات بعد.",
  errorBody: "تحقق من الاتصال وحاول مرة أخرى.",
  errorTitle: "تعذر تحديث الإشعارات",
  food_order: "الطلبات",
  food_orderDescription: "تقدم طلبات الطعام من الصالة.",
  food_orderUpdate: "تحديث طلب",
  inbox: "الوارد",
  inboxAlwaysOn: "تظل سجلات الوارد متاحة حتى عند إيقاف التنبيهات.",
  invalidTarget: "لا توجد وجهة آمنة لهذا التحديث. سيبقى هنا للرجوع إليه.",
  justNow: "الآن",
  loadEarlier: "تحميل إشعارات أقدم",
  loadMoreFailed: "تعذر تحميل الإشعارات الأقدم.",
  loading: "جار تحميل الإشعارات…",
  loadingEarlier: "جار تحميل إشعارات أقدم…",
  markAllRead: "تحديد الكل كمقروء",
  markRead: "تحديد كمقروء",
  notificationViews: "عروض الإشعارات",
  notifications: "الإشعارات",
  openBooking: "فتح الحجز",
  openDetails: "مراجعة التفاصيل",
  openOrder: "فتح الطلب",
  openSession: "فتح الجلسة",
  openVenue: "فتح المكان",
  pendingBody: "لن يتمكن العملاء من تسجيل الحضور قبل تأكيد الحجوزات المتبقية.",
  pendingEyebrow: "يتطلب إجراء",
  pendingMany: "{count} حجوزات بانتظار التأكيد",
  pendingOne: "حجز واحد بانتظار التأكيد",
  preferenceError: "تعذر حفظ إعدادات التنبيهات. ما زالت اختياراتك السابقة مطبقة.",
  preferenceLoadError: "تعذر تحميل إعدادات التنبيهات.",
  pushAlerts: "تنبيهات الجهاز",
  pushBody: "اختر التحديثات التي تصل إلى هذا الحساب خارج مساحة العمل.",
  pushLoading: "جار تحميل إعدادات التنبيهات…",
  pushTitle: "إعدادات التنبيهات",
  refresh: "تحديث الإشعارات",
  refreshing: "جار تحديث الإشعارات…",
  retry: "حاول مرة أخرى",
  review: "مراجعة",
  reviewPending: "مراجعة الحجوزات",
  saved: "إعدادات التنبيهات محدّثة.",
  saving: "جار حفظ إعداد التنبيه…",
  session: "الجلسات",
  sessionDescription: "بدء الجلسات وتمديدها وإنهاؤها.",
  sessionUpdate: "تحديث جلسة",
  staleTarget: "لم يعد هذا التحديث قابلاً للإجراء. سيبقى هنا للرجوع إليه.",
  turnOff: "إيقاف تنبيهات {category}",
  turnOn: "تشغيل تنبيهات {category}",
  unauthorizedTarget: "تغيرت صلاحية الوصول، لذلك لم تعد هذه الوجهة متاحة.",
  unavailable: "غير متاح",
  unread: "{count} غير مقروء",
};

export function providerNotificationCopy(
  language: AppLanguage,
  key: ProviderNotificationCopyKey,
  values?: Record<string, number | string>,
) {
  const template = language === "ar" ? arabicCopy[key] : englishCopy[key];
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) =>
    values[name] === undefined ? match : String(values[name]),
  );
}
